import { z } from 'zod';
import { priceObservationSchema } from './prices.js';
import { cursorPageSchema, cursorQuerySchema, decimalStringSchema, isoDateTimeSchema, nonNegativeDecimalStringSchema, uuidSchema, localeSchema, } from './common.js';
import { GROCERY_CATEGORIES, groceryCategorySchema } from './enums.js';
import { bboxSchema, latitudeSchema, longitudeSchema, placeProviderSchema } from './tasting.js';
/**
 * Ingredient catalog, products, stores and purchase hints.
 * See docs/finished/ingredient-catalog-spec.md. Quantities and nutrition values are decimal strings.
 */
export const CATALOG_MAX_ALIASES = 30;
export const CATALOG_MAX_PRODUCT_PHOTOS = 4;
export const CATALOG_SYNC_PAGE_SIZE = 500;
export const PREMIUM_NUTRITION_ENTITLEMENT = 'nutrition.premium';
export const catalogScopeSchema = z.enum(['GLOBAL', 'HOUSEHOLD', 'USER']);
export const productSourceSchema = z.enum(['OPEN_FOOD_FACTS', 'LABEL_SCAN', 'USER']);
export const nutritionSourceSchema = z.enum(['OPEN_FOOD_FACTS', 'LABEL_SCAN', 'USER', 'PROVIDER']);
export const nutritionBasisSchema = z.enum(['PER_100_G', 'PER_100_ML']);
export const nutriScoreGradeSchema = z.enum(['A', 'B', 'C', 'D', 'E']);
export const hintVerdictSchema = z.enum(['RECOMMENDED', 'AVOID', 'NOTE']);
export const storeKindSchema = z.enum(['CHAIN', 'BRANCH']);
export const providerTierSchema = z.enum(['FREE', 'PREMIUM']);
export const ingredientLinkSourceSchema = z.enum(['AUTO', 'USER']);
export const productMediaRoleSchema = z.enum(['FRONT', 'LABEL']);
const shortText = (max) => z.string().trim().min(1).max(max);
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
/** GTIN-8/12/13/14, digits only. The check digit is validated in the domain layer. */
export const barcodeSchema = z
    .string()
    .trim()
    .regex(/^\d{8}$|^\d{12,14}$/, 'Expected a GTIN with 8, 12, 13 or 14 digits');
// ---------------------------------------------------------------------------
// Nutrition
// ---------------------------------------------------------------------------
export const NUTRIENT_KEYS = [
    'energyKj',
    'energyKcal',
    'fat',
    'saturatedFat',
    'carbohydrates',
    'sugars',
    'fibre',
    'protein',
    'salt',
];
const nutrientValue = nonNegativeDecimalStringSchema.nullable();
export const nutritionValuesSchema = z.object({
    energyKj: nutrientValue,
    energyKcal: nutrientValue,
    fat: nutrientValue,
    saturatedFat: nutrientValue,
    carbohydrates: nutrientValue,
    sugars: nutrientValue,
    fibre: nutrientValue,
    protein: nutrientValue,
    salt: nutrientValue,
});
export const nutritionFactsSchema = nutritionValuesSchema.extend({
    basis: nutritionBasisSchema,
    portionQuantity: decimalStringSchema.nullable(),
    portionUnitText: z.string().nullable(),
    source: nutritionSourceSchema,
    providerKey: z.string().nullable(),
    tier: providerTierSchema,
    /** Attribution text that has to be shown with the values (ODbL etc.). */
    attribution: z.string().nullable(),
    capturedAt: isoDateTimeSchema,
});
export const nutritionWriteSchema = nutritionValuesSchema.partial().extend({
    basis: nutritionBasisSchema.default('PER_100_G'),
    portionQuantity: nonNegativeDecimalStringSchema.nullable().optional(),
    portionUnitText: optionalText(40),
});
// ---------------------------------------------------------------------------
// Hints
// ---------------------------------------------------------------------------
export const hintSummarySchema = z.object({
    recommended: z.number().int(),
    avoid: z.number().int(),
    note: z.number().int(),
    /** Average of the 1–5 star ratings, one decimal; null without ratings. */
    averageRating: z.number().nullable(),
});
export const purchaseHintSchema = z.object({
    id: uuidSchema,
    householdId: uuidSchema.nullable(),
    ingredientId: uuidSchema.nullable(),
    productId: uuidSchema.nullable(),
    storeId: uuidSchema.nullable(),
    /** Denormalized for lists and the offline copy. */
    storeName: z.string().nullable(),
    targetName: z.string().nullable(),
    verdict: hintVerdictSchema,
    rating: z.number().int().min(1).max(5).nullable(),
    text: z.string(),
    authorId: uuidSchema.nullable(),
    authorName: z.string().nullable(),
    own: z.boolean(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
const hintFields = {
    storeId: uuidSchema.nullable().optional(),
    verdict: hintVerdictSchema,
    rating: z.number().int().min(1).max(5).nullable().optional(),
    text: z.string().trim().max(1000).default(''),
};
export const purchaseHintWriteSchema = z
    .object({
    /** Optional client-generated id so hints written offline keep their identity. */
    id: uuidSchema.optional(),
    /** Household the hint is shared with; null/omitted = only me. */
    householdId: uuidSchema.nullable().optional(),
    ingredientId: uuidSchema.nullable().optional(),
    productId: uuidSchema.nullable().optional(),
    ...hintFields,
})
    .refine((v) => !!v.ingredientId !== !!v.productId, {
    message: 'A hint targets exactly one of ingredient or product',
    path: ['ingredientId'],
})
    .refine((v) => v.verdict !== 'NOTE' || v.text.length > 0, {
    message: 'A note needs a text',
    path: ['text'],
});
export const purchaseHintUpdateSchema = z.object({
    storeId: uuidSchema.nullable().optional(),
    verdict: hintVerdictSchema.optional(),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    text: z.string().trim().max(1000).optional(),
});
export const purchaseHintListQuerySchema = z.object({
    ingredientId: uuidSchema.optional(),
    productId: uuidSchema.optional(),
    storeId: uuidSchema.optional(),
});
// ---------------------------------------------------------------------------
// Ingredients
// ---------------------------------------------------------------------------
export const ingredientNamesSchema = z.object({
    de: z.string().trim().max(120).optional(),
    en: z.string().trim().max(120).optional(),
});
export const ingredientAliasSchema = z.object({
    id: uuidSchema,
    locale: z.string(),
    name: z.string(),
});
export const ingredientAliasWriteSchema = z.object({
    locale: localeSchema.default('de'),
    name: shortText(120),
});
/** Compact projection used in links, resolution results and the offline copy. */
export const ingredientRefSchema = z.object({
    id: uuidSchema,
    scope: catalogScopeSchema,
    name: z.string(),
    names: ingredientNamesSchema,
    normalizedName: z.string(),
    groceryCategory: groceryCategorySchema,
});
export const ingredientSchema = ingredientRefSchema.extend({
    householdId: uuidSchema.nullable(),
    countsAsFruitVeg: z.boolean().nullable(),
    aliases: z.array(ingredientAliasSchema),
    nutrition: nutritionFactsSchema.nullable(),
    hintSummary: hintSummarySchema,
    productCount: z.number().int(),
    /** False for GLOBAL rows and for rows of a household the actor may only read. */
    editable: z.boolean(),
    version: z.number().int(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const ingredientWriteSchema = z.object({
    id: uuidSchema.optional(),
    householdId: uuidSchema.nullable().optional(),
    name: shortText(120),
    names: ingredientNamesSchema.optional(),
    groceryCategory: groceryCategorySchema.optional(),
    countsAsFruitVeg: z.boolean().nullable().optional(),
    aliases: z.array(ingredientAliasWriteSchema).max(CATALOG_MAX_ALIASES).optional(),
    nutrition: nutritionWriteSchema.nullable().optional(),
});
export const ingredientUpdateSchema = ingredientWriteSchema
    .omit({ id: true, householdId: true })
    .partial()
    .extend({ version: z.number().int().nonnegative() });
export const ingredientListQuerySchema = cursorQuerySchema.extend({
    q: z.string().trim().max(120).optional(),
    category: groceryCategorySchema.optional(),
    scope: z.enum(['all', 'own', 'global']).default('all'),
    /** Only ingredients the reader's household (or the reader) has purchase hints for. */
    hints: z.enum(['any', 'recommended', 'avoid']).optional(),
    /** With or without nutrition values the reader may see. */
    nutrition: z.enum(['with', 'without']).optional(),
});
export const ingredientPageSchema = cursorPageSchema(ingredientSchema);
export const ingredientMergeRequestSchema = z.object({ sourceIngredientId: uuidSchema });
export const ingredientResolveQuerySchema = z.object({
    /** Comma separated names (at most 100). */
    names: z
        .string()
        .max(8000)
        .transform((v) => v
        .split(',')
        .map((n) => n.trim())
        .filter(Boolean)
        .slice(0, 100)),
    householdId: uuidSchema.optional(),
});
export const ingredientResolveResponseSchema = z.object({
    matches: z.array(z.object({
        name: z.string(),
        normalizedName: z.string(),
        ingredient: ingredientRefSchema.nullable(),
    })),
});
/** Names that occur often but are not linked to any ingredient. */
export const unlinkedNameSchema = z.object({
    normalizedName: z.string(),
    name: z.string(),
    recipeCount: z.number().int(),
});
/** Link of a recipe ingredient, shopping item or pantry item, resolved for the reader. */
export const ingredientLinkSchema = z.object({
    ingredient: ingredientRefSchema.nullable(),
    source: ingredientLinkSourceSchema.nullable(),
});
export const ingredientLinkUpdateSchema = z.object({
    /** null = "no ingredient" chosen by the user; never re-resolved automatically. */
    ingredientId: uuidSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Products
// ---------------------------------------------------------------------------
export const productPhotoSchema = z.object({
    assetId: uuidSchema,
    role: productMediaRoleSchema,
    url: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    status: z.string(),
});
export const productPhotoInputSchema = z.object({
    assetId: uuidSchema,
    role: productMediaRoleSchema.default('FRONT'),
});
export const productSchema = z.object({
    id: uuidSchema,
    scope: catalogScopeSchema,
    householdId: uuidSchema.nullable(),
    barcode: z.string().nullable(),
    brand: z.string().nullable(),
    name: z.string(),
    ingredient: ingredientRefSchema.nullable(),
    packageQuantity: decimalStringSchema.nullable(),
    packageUnitCode: z.string().nullable(),
    packageUnitText: z.string().nullable(),
    ingredientsText: z.string().nullable(),
    allergens: z.array(z.string()),
    additives: z.array(z.string()),
    labels: z.array(z.string()),
    nutriScore: nutriScoreGradeSchema.nullable(),
    nutriScoreSource: productSourceSchema.nullable(),
    /** True when the user recorded that no Nutri-Score is printed on the package. */
    nutriScoreNone: z.boolean(),
    novaGroup: z.number().int().nullable(),
    ecoScore: z.string().nullable(),
    nutrition: nutritionFactsSchema.nullable(),
    source: productSourceSchema,
    /** Attribution that must be displayed with the data (Open Food Facts, ODbL). */
    attribution: z.string().nullable(),
    photos: z.array(productPhotoSchema),
    shadowsProductId: uuidSchema.nullable(),
    hintSummary: hintSummarySchema,
    editable: z.boolean(),
    version: z.number().int(),
    fetchedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
const productFields = {
    brand: optionalText(120),
    name: shortText(200),
    ingredientId: uuidSchema.nullable().optional(),
    packageQuantity: nonNegativeDecimalStringSchema.nullable().optional(),
    packageUnitCode: z.string().trim().max(32).nullable().optional(),
    packageUnitText: optionalText(40),
    ingredientsText: optionalText(4000),
    allergens: z.array(shortText(80)).max(40).optional(),
    additives: z.array(shortText(80)).max(80).optional(),
    labels: z.array(shortText(80)).max(40).optional(),
    nutriScore: nutriScoreGradeSchema.nullable().optional(),
    /** Records "no score printed" so the app stops asking. */
    nutriScoreNone: z.boolean().optional(),
    novaGroup: z.number().int().min(1).max(4).nullable().optional(),
    nutrition: nutritionWriteSchema.nullable().optional(),
    photos: z.array(productPhotoInputSchema).max(CATALOG_MAX_PRODUCT_PHOTOS).optional(),
    /** LABEL_SCAN when the values of this request were confirmed from a label scan. */
    dataSource: z.enum(['USER', 'LABEL_SCAN']).optional(),
};
export const productWriteSchema = z.object({
    /** Client-generated id for products captured offline (also the idempotency key). */
    id: uuidSchema.optional(),
    householdId: uuidSchema.nullable().optional(),
    barcode: barcodeSchema.nullable().optional(),
    ...productFields,
});
export const productUpdateSchema = z
    .object({
    ...productFields,
    name: shortText(200).optional(),
    /** Household that receives the shadow row when a GLOBAL product is edited. */
    householdId: uuidSchema.nullable().optional(),
    version: z.number().int().nonnegative(),
})
    .strict();
export const productListQuerySchema = cursorQuerySchema.extend({
    q: z.string().trim().max(120).optional(),
    ingredientId: uuidSchema.optional(),
    storeId: uuidSchema.optional(),
    verdict: hintVerdictSchema.optional(),
});
export const productPageSchema = cursorPageSchema(productSchema);
export const productLookupQuerySchema = z.object({
    householdId: uuidSchema.optional(),
});
export const productLookupResponseSchema = z.object({
    product: productSchema,
    /** Where the answer came from: the household, the shared cache or a fresh provider call. */
    origin: z.enum(['HOUSEHOLD', 'CACHE', 'PROVIDER']),
    /** Hints for the product and for its ingredient, newest first. */
    hints: z.array(purchaseHintSchema),
    /** Matches between the ingredients text/allergens and the user's dietary settings. */
    conflicts: z.array(z.object({ term: z.string(), kind: z.enum(['AVOIDED_INGREDIENT', 'DIETARY_PREFERENCE']) })),
});
export const productMergeRequestSchema = z.object({ sourceProductId: uuidSchema });
export const labelScanRequestSchema = z.object({
    assetIds: z.array(uuidSchema).min(1).max(3),
    language: localeSchema.optional(),
});
/** Input stored on the AIGeneration row for PRODUCT_LABEL_EXTRACTION. */
export const aiLabelExtractionInputSchema = z.object({
    productId: uuidSchema.nullable().default(null),
    language: localeSchema.default('de'),
});
export const labelNutritionRowSchema = z.object({
    nutrient: z.enum(NUTRIENT_KEYS),
    per100: nonNegativeDecimalStringSchema.nullable(),
    perPortion: nonNegativeDecimalStringSchema.nullable(),
    /** Set by the plausibility check; flagged rows need a look before saving. */
    implausible: z.boolean().default(false),
    reason: z.string().nullable().default(null),
});
/** Output of PRODUCT_LABEL_EXTRACTION – a proposal, nothing is stored without confirmation. */
export const labelExtractionResultSchema = z.object({
    productId: uuidSchema.nullable(),
    ingredientsText: z.string().nullable(),
    allergens: z.array(z.string()),
    additives: z.array(z.string()),
    basis: nutritionBasisSchema,
    nutrition: z.array(labelNutritionRowSchema),
    portionQuantity: nonNegativeDecimalStringSchema.nullable(),
    portionUnitText: z.string().nullable(),
    packageQuantity: nonNegativeDecimalStringSchema.nullable(),
    packageUnitText: z.string().nullable(),
    /** Only when the logo is visible on a photo; never inferred from the numbers. */
    nutriScore: nutriScoreGradeSchema.nullable(),
    unreadable: z.array(z.string()),
    transcript: z.string(),
});
// ---------------------------------------------------------------------------
// Stores
// ---------------------------------------------------------------------------
export const storeSchema = z.object({
    id: uuidSchema,
    householdId: uuidSchema.nullable(),
    kind: storeKindSchema,
    parentStoreId: uuidSchema.nullable(),
    parentName: z.string().nullable(),
    name: z.string(),
    lat: z.number().nullable(),
    lng: z.number().nullable(),
    street: z.string().nullable(),
    postalCode: z.string().nullable(),
    city: z.string().nullable(),
    countryCode: z.string().nullable(),
    provider: placeProviderSchema.nullable(),
    providerRef: z.string().nullable(),
    note: z.string().nullable(),
    /** The household's rating of the store, 1–5 stars; the comment is the note. */
    rating: z.number().int().min(1).max(5).nullable(),
    /** The loyalty card shown at this store's checkout (absent on older servers). */
    loyaltyCardId: uuidSchema.nullable().default(null),
    /**
     * The departments in the order this store is walked through; empty = none recorded (a branch
     * then uses its chain's, else the default order). Codes this client does not know are kept.
     */
    departmentOrder: z.array(z.string()).optional(),
    hintCount: z.number().int(),
    branchCount: z.number().int(),
    editable: z.boolean(),
    version: z.number().int(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
const storeFields = {
    kind: storeKindSchema.default('CHAIN'),
    parentStoreId: uuidSchema.nullable().optional(),
    /** Convenience: creates or reuses the chain with this name as parent of a branch. */
    chainName: optionalText(120),
    name: shortText(160),
    lat: latitudeSchema.nullable().optional(),
    lng: longitudeSchema.nullable().optional(),
    street: optionalText(200),
    postalCode: optionalText(20),
    city: optionalText(120),
    countryCode: z
        .string()
        .trim()
        .regex(/^[A-Za-z]{2}$/)
        .transform((v) => v.toUpperCase())
        .nullable()
        .optional(),
    provider: placeProviderSchema.nullable().optional(),
    providerRef: z
        .string()
        .trim()
        .regex(/^(node|way|relation)\/\d{1,20}$/)
        .nullable()
        .optional(),
    note: optionalText(500),
    rating: z.number().int().min(1).max(5).nullable().optional(),
    /** A loyalty card of the same scope; null takes it off the store. */
    loyaltyCardId: uuidSchema.nullable().optional(),
    /** Every department at most once; departments left out follow in the default order. */
    departmentOrder: z
        .array(groceryCategorySchema)
        .max(GROCERY_CATEGORIES.length)
        .refine((v) => new Set(v).size === v.length, { message: 'A department appears twice' })
        .optional(),
};
export const storeWriteSchema = z
    .object({
    id: uuidSchema.optional(),
    householdId: uuidSchema.nullable().optional(),
    ...storeFields,
})
    .refine((v) => (v.lat == null) === (v.lng == null), {
    message: 'lat and lng belong together',
    path: ['lat'],
})
    .refine((v) => v.kind === 'BRANCH' || (v.lat == null && !v.parentStoreId && !v.chainName), {
    message: 'A chain has no position and no parent',
    path: ['kind'],
});
export const storeUpdateSchema = z.object({
    parentStoreId: uuidSchema.nullable().optional(),
    name: shortText(160).optional(),
    lat: latitudeSchema.nullable().optional(),
    lng: longitudeSchema.nullable().optional(),
    street: optionalText(200),
    postalCode: optionalText(20),
    city: optionalText(120),
    countryCode: storeFields.countryCode,
    note: optionalText(500),
    rating: storeFields.rating,
    loyaltyCardId: storeFields.loyaltyCardId,
    departmentOrder: storeFields.departmentOrder,
    version: z.number().int().nonnegative(),
});
export const storeListQuerySchema = z.object({
    kind: storeKindSchema.optional(),
    bbox: bboxSchema.optional(),
    q: z.string().trim().max(120).optional(),
});
export const storeMergeRequestSchema = z.object({ sourceStoreId: uuidSchema });
export const storeDeleteQuerySchema = z.object({
    /** keep = hints become store-independent (default); delete = hints are removed too. */
    hints: z.enum(['keep', 'delete']).default('keep'),
});
// ---------------------------------------------------------------------------
// Hints on a shopping list
// ---------------------------------------------------------------------------
export const hintMarkerSchema = z.enum(['AVOID', 'RECOMMENDED', 'NOTE', 'NONE']);
export const shoppingItemHintsSchema = z.object({
    itemId: uuidSchema,
    ingredient: ingredientRefSchema.nullable(),
    product: z
        .object({
        id: uuidSchema,
        name: z.string(),
        brand: z.string().nullable(),
        nutriScore: nutriScoreGradeSchema.nullable(),
        thumbnailUrl: z.string().nullable(),
    })
        .nullable(),
    /** Overall marker for the selected store (most specific applicable hint wins). */
    marker: hintMarkerSchema,
    /** Hints that apply in the selected store or everywhere, most specific first. */
    applicable: z.array(purchaseHintSchema),
    /** Hints that only apply in other stores. */
    elsewhere: z.array(purchaseHintSchema),
});
export const shoppingListHintsQuerySchema = z.object({ storeId: uuidSchema.optional() });
export const shoppingListHintsResponseSchema = z.object({
    storeId: uuidSchema.nullable(),
    items: z.array(shoppingItemHintsSchema),
});
// ---------------------------------------------------------------------------
// Loyalty cards (user, 2026-09-25: Payback)
// ---------------------------------------------------------------------------
/** The picture of a card: the large view for the checkout scanner and a small one for lists. */
export const loyaltyCardImageSchema = z.object({
    assetId: uuidSchema,
    url: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    status: z.string(),
});
export const loyaltyCardSchema = z.object({
    id: uuidSchema,
    householdId: uuidSchema.nullable(),
    name: z.string(),
    image: loyaltyCardImageSchema.nullable(),
    /** Stores this card is shown at. */
    storeIds: z.array(uuidSchema),
    editable: z.boolean(),
    version: z.number().int(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const loyaltyCardWriteSchema = z.object({
    /** Client-generated id for cards captured offline (also the idempotency key). */
    id: uuidSchema.optional(),
    householdId: uuidSchema.nullable().optional(),
    name: shortText(120),
    /** A media asset of purpose LOYALTY_CARD uploaded by the caller. */
    imageAssetId: uuidSchema.nullable().optional(),
});
export const loyaltyCardUpdateSchema = z.object({
    name: shortText(120).optional(),
    imageAssetId: uuidSchema.nullable().optional(),
    version: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// Device copy (delta sync)
// ---------------------------------------------------------------------------
export const catalogEntitySchema = z.enum([
    'ingredient',
    'product',
    'store',
    'hint',
    'price',
    'loyaltyCard',
]);
export const catalogSyncQuerySchema = z.object({
    /** Opaque cursor of the previous sync; omit for a full sync. */
    since: z.string().max(200).optional(),
});
export const catalogSyncResponseSchema = z.object({
    /** True when the client has to drop its copy first (first sync, scope change, stale cursor). */
    full: z.boolean(),
    ingredients: z.array(ingredientSchema),
    products: z.array(productSchema),
    stores: z.array(storeSchema),
    hints: z.array(purchaseHintSchema),
    /** Prices of the last 24 months (absent on older servers). */
    priceObservations: z.array(priceObservationSchema).default([]),
    /** Loyalty cards of the household (absent on older servers). */
    loyaltyCards: z.array(loyaltyCardSchema).default([]),
    tombstones: z.array(z.object({ entity: catalogEntitySchema, id: uuidSchema })),
    /** Dietary settings, so conflict warnings work offline. */
    dietary: z.object({
        dietaryPreferences: z.array(z.string()),
        avoidedIngredients: z.array(z.string()),
    }),
    /** Pass as `since` next time. */
    cursor: z.string(),
    /** More rows are waiting; call again with `cursor` right away. */
    hasMore: z.boolean(),
    syncedAt: isoDateTimeSchema,
});
// ---------------------------------------------------------------------------
// Recipe nutrition
// ---------------------------------------------------------------------------
export const recipeNutritionIngredientSchema = z.object({
    recipeIngredientId: uuidSchema,
    name: z.string(),
    ingredient: ingredientRefSchema.nullable(),
    grams: decimalStringSchema.nullable(),
    /** Why the ingredient did not count: no link, no nutrition values or no convertible quantity. */
    status: z.enum(['COUNTED', 'NO_INGREDIENT', 'NO_NUTRITION', 'NO_QUANTITY']),
    nutriScore: nutriScoreGradeSchema.nullable(),
});
export const recipeNutritionSchema = z.object({
    recipeId: uuidSchema,
    servings: decimalStringSchema,
    perServing: nutritionValuesSchema.nullable(),
    per100g: nutritionValuesSchema.nullable(),
    totalWeightGrams: decimalStringSchema.nullable(),
    coverage: z.object({
        counted: z.number().int(),
        total: z.number().int(),
        /** Share of the ingredient weight that has nutrition values, 0–100. */
        weightPercent: z.number().int(),
    }),
    /** The app's own estimate; null when the coverage is below the configured minimum. */
    nutriScoreEstimate: nutriScoreGradeSchema.nullable(),
    algorithmVersion: z.string(),
    minCoveragePercent: z.number().int(),
    providers: z.array(z.object({ key: z.string(), attribution: z.string().nullable() })),
    ingredients: z.array(recipeNutritionIngredientSchema),
    /** Provider lookups for missing values were queued; ask again in a moment. */
    pending: z.boolean(),
});
// ---------------------------------------------------------------------------
// Administration
// ---------------------------------------------------------------------------
export const NUTRITION_PROVIDER_KEYS = ['openfoodfacts', 'usda', 'bls', 'mock'];
export const nutritionProviderKeySchema = z.enum(NUTRITION_PROVIDER_KEYS);
export const nutritionProviderConfigSchema = z.object({
    key: nutritionProviderKeySchema,
    label: z.string(),
    enabled: z.boolean(),
    /** An adapter exists in this build. `bls` is reserved until a licence is available. */
    available: z.boolean(),
    needsCredentials: z.boolean(),
    hasCredentials: z.boolean(),
    priority: z.number().int(),
    tier: providerTierSchema,
    attribution: z.string().nullable(),
    dailyLimit: z.number().int().nullable(),
    requestsToday: z.number().int(),
    updatedAt: isoDateTimeSchema.nullable(),
});
export const nutritionProviderUpdateSchema = z.object({
    enabled: z.boolean().optional(),
    priority: z.number().int().min(0).max(100).optional(),
    tier: providerTierSchema.optional(),
    attribution: z.string().trim().max(300).nullable().optional(),
    dailyLimit: z.number().int().min(1).max(1_000_000).nullable().optional(),
});
export const nutritionProviderTestResponseSchema = z.object({
    ok: z.boolean(),
    message: z.string(),
    sample: nutritionValuesSchema.nullable(),
});
export const userEntitlementsSchema = z.object({
    userId: uuidSchema,
    entitlements: z.array(z.object({
        key: z.string(),
        source: z.string(),
        validUntil: isoDateTimeSchema.nullable(),
    })),
});
export const userEntitlementsUpdateSchema = z.object({
    keys: z.array(z.enum([PREMIUM_NUTRITION_ENTITLEMENT])).max(10),
    validUntil: isoDateTimeSchema.nullable().optional(),
});
export const catalogBackfillStatusSchema = z.object({
    state: z.enum(['IDLE', 'RUNNING', 'DONE', 'FAILED']),
    processed: z.number().int(),
    linked: z.number().int(),
    startedAt: isoDateTimeSchema.nullable(),
    finishedAt: isoDateTimeSchema.nullable(),
    message: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Links of a recipe's ingredients, resolved for the reader
// ---------------------------------------------------------------------------
export const recipeIngredientLinkSchema = z.object({
    recipeIngredientId: uuidSchema,
    name: z.string(),
    ingredient: ingredientRefSchema.nullable(),
    source: ingredientLinkSourceSchema.nullable(),
    marker: hintMarkerSchema,
    /** The product the reader's household recommends for this ingredient, if any. */
    product: z
        .object({
        id: uuidSchema,
        name: z.string(),
        brand: z.string().nullable(),
        nutriScore: nutriScoreGradeSchema.nullable(),
    })
        .nullable(),
});
export const recipeIngredientLinksResponseSchema = z.object({
    links: z.array(recipeIngredientLinkSchema),
});
//# sourceMappingURL=catalog.js.map